import clsx from 'clsx';
import * as React from 'react';

import * as AccordionPrimitive from '@radix-ui/react-accordion';

import { DropdownIcon, IconChevronDown, MinusIcon, PlusIcon } from '@/assets/icons';
import { cn } from '@/lib/utils';

interface AccordionTriggerProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> {
  iconWidth?: number | string;
  iconHeight?: number | string;
  isTerms?: boolean;
  triggerIconClassName?: any;
  isMenu?: boolean;
  isSmallOrMedium?: boolean;
  menuState?: boolean;
}

interface AccordionContentProps {
  onMouseEnter?: any;
  onMouseLeave?: any;
}
const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item ref={ref} className={cn('border-b', className)} {...props} />
));
AccordionItem.displayName = 'AccordionItem';

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  AccordionTriggerProps
>(
  (
    {
      className,
      iconWidth,
      iconHeight,
      isTerms,
      triggerIconClassName,
      isMenu,
      isSmallOrMedium,
      menuState,
      children,
      ...props
    },
    ref
  ) => {
    const [accordionState, setAccordionState] = React.useState(false);
    return (
      <AccordionPrimitive.Header className="flex">
        <AccordionPrimitive.Trigger
          ref={ref}
          className={cn(
            'font-skyBold flex w-full flex-1 items-center justify-between py-4 text-xl font-bold transition-all',
            className,
            {
              '[&[data-state=open]>svg]:rotate-180':
                !isMenu || (isMenu && isSmallOrMedium && menuState),
            }
          )}
          onClick={() => setAccordionState(!accordionState)}
          data-testid="testIdMenu"
          {...props}
        >
          {children}
          {isTerms ? (
            <div className={clsx(triggerIconClassName.dimension, `flex shrink-0 justify-end`)}>
              {accordionState ? (
                <MinusIcon
                  className={clsx(
                    `duration 300 text-slate-800 transition-transform`,
                    triggerIconClassName.dimension
                  )}
                />
              ) : (
                <PlusIcon
                  className={clsx(
                    triggerIconClassName.dimension,
                    'duration 300 text-slate-800 transition-transform'
                  )}
                />
              )}
            </div>
          ) : isMenu ? (
            <DropdownIcon
              className={clsx('shrink-0', triggerIconClassName, {
                '-rotate-90': isMenu && isSmallOrMedium && !menuState,
              })}
            />
          ) : (
            <IconChevronDown
              width={iconWidth}
              height={iconHeight}
              className="size-[27px] shrink-0 transition-transform duration-200 md:size-[35.1px] lg:size-[45.63px]"
            />
          )}
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>
    );
  }
);
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content> & AccordionContentProps,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, onMouseEnter, onMouseLeave, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down text-lg font-normal"
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    {...props}
  >
    <div className={cn('overflow-hidden pb-4 pl-4 pt-0', className)}>{children}</div>
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
